import React, { useState, useRef } from "react";
import { Grid3X3, Loader2, Wand2, Minus, Plus, Pencil, Eye, Scissors, ZoomIn, ExternalLink, RefreshCw } from "lucide-react";

const ASPECT_RATIOS = ["16:9", "9:16", "1:1", "4:3", "3:4"];
const ACCENT = 'var(--color-accent-primary)';
// 主题变量无法拼 hex-alpha，统一用 color-mix 生成浅色底
const accentBg = (pct = 12) => `color-mix(in srgb, ${ACCENT} ${pct}%, transparent)`;

export default function GridEditPanel({ data, id, onClose }) {
  const [rows, setRows] = useState(data.rows || 3);
  const [cols, setCols] = useState(data.cols || 3);
  const [aspectRatio, setAspectRatio] = useState(data.aspectRatio || "16:9");
  const [prompts, setPrompts] = useState(data.prompts || []);
  const [imagePrompt] = useState(data.imagePrompt || "");
  const [viewMode, setViewMode] = useState(data.viewMode || "edit");
  const [focusedIndex, setFocusedIndex] = useState(null);
  const textareaRefs = useRef([]);

  const splitCellImages = data.splitCellImages || [];
  const gridImageUrl = data.gridImageUrl || null;
  const connectedImages = data.connectedImages || [];
  const isGenerating = data.isGenerating;
  const isSplitting = data.isSplitting;
  const hasSplitImages = splitCellImages.length > 0;
  const hasGridImage = !!gridImageUrl;

  // 行列变化时按新格数补齐/截断 prompts（渲染期调整，避免 effect 级联渲染）
  const totalCells = rows * cols;
  const gridKey = `${rows}x${cols}`;
  const [lastGridKey, setLastGridKey] = useState(gridKey);
  if (gridKey !== lastGridKey) {
    setLastGridKey(gridKey);
    setPrompts(prev => Array(totalCells).fill("").map((_, i) => prev[i] || ""));
  }

  const syncToData = (extra = {}) => {
    data.onUpdate?.(id, { ...data, rows, cols, aspectRatio, prompts, imagePrompt, viewMode, ...extra });
  };

  const handleGenerate = () => {
    syncToData();
    data.onGenerate?.(id, { ...data, rows, cols, aspectRatio, prompts, imagePrompt });
  };

  const handleSplitGrid = () => {
    const url = gridImageUrl || data.gridImageUrl;
    if (!url) return;
    data.onSplitGrid?.(id, { gridImageUrl: url, rows, cols });
  };

  const handleRegenerateCell = (cellIndex) => {
    const prompt = prompts[cellIndex];
    if (!prompt?.trim()) return;
    data.onUpdate?.(id, { ...data, pendingRegenerateCell: { cellIndex, prompt, aspectRatio } });
  };

  const handleExportCell = (cellIndex) => {
    const cell = splitCellImages.find(c => c.index === cellIndex);
    if (!cell) return;
    data.onUpdate?.(id, { ...data, pendingExportCell: { cellIndex, url: cell.url, name: `格${cellIndex + 1}` } });
  };

  const handleUpscaleCell = (cellIndex) => {
    const cell = splitCellImages.find(c => c.index === cellIndex);
    if (!cell) return;
    data.onUpdate?.(id, { ...data, pendingUpscaleCell: { cellIndex, url: cell.url } });
  };

  const insertRefName = (name) => {
    if (focusedIndex === null) return;
    const textarea = textareaRefs.current[focusedIndex];
    if (!textarea) return;
    const start = textarea.selectionStart;
    const text = prompts[focusedIndex] || "";
    const insert = `"${name}"`;
    const newPrompts = [...prompts];
    newPrompts[focusedIndex] = text.substring(0, start) + insert + text.substring(start);
    setPrompts(newPrompts);
  };

  const filledCount = prompts.filter(p => p?.trim()).length;

  return (
    <div
      className="nodrag nowheel rounded-lg"
      style={{
        background: "var(--color-bg-secondary)",
        border: "1px solid var(--color-border-primary)",
        marginTop: 6,
        maxWidth: 480,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* 头部 */}
      <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: "1px solid var(--color-border-secondary)" }}>
        <div className="flex items-center gap-2">
          <Grid3X3 size={13} style={{ color: ACCENT }} />
          <span className="text-xs font-bold" style={{ color: "var(--color-text-primary)" }}>
            {data.templateName || '分镜生成'} · <span className="font-mono-num">{rows}×{cols}</span>
          </span>
          <span className="text-2xs font-mono-num" style={{ color: "var(--color-text-muted)" }}>
            {filledCount}/{totalCells}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {(hasGridImage || hasSplitImages) && (
            <>
              <button title="编辑分镜描述" onClick={() => setViewMode("edit")} className="px-1.5 py-0.5 rounded-md text-2xs" style={{ background: viewMode === "edit" ? accentBg(15) : "transparent", color: viewMode === "edit" ? ACCENT : "var(--color-text-muted)" }}>
                <Pencil size={10} />
              </button>
              {hasGridImage && (
                <button title="预览宫格图" onClick={() => setViewMode("preview")} className="px-1.5 py-0.5 rounded-md text-2xs" style={{ background: viewMode === "preview" ? accentBg(15) : "transparent", color: viewMode === "preview" ? ACCENT : "var(--color-text-muted)" }}>
                  <Eye size={10} />
                </button>
              )}
              {hasSplitImages && (
                <button title="查看拆分结果" onClick={() => setViewMode("generated")} className="px-1.5 py-0.5 rounded-md text-2xs" style={{ background: viewMode === "generated" ? accentBg(15) : "transparent", color: viewMode === "generated" ? ACCENT : "var(--color-text-muted)" }}>
                  <Grid3X3 size={10} />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* 参数行 */}
      <div className="flex items-center gap-1.5 px-3 py-1.5 flex-wrap" style={{ borderBottom: "1px solid var(--color-border-secondary)" }}>
        {/* 行列调整 */}
        <div className="flex items-center gap-1">
          <button title="减少行数" onClick={() => rows > 1 && setRows(rows - 1)} className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}><Minus size={10} /></button>
          <span className="text-2xs font-mono-num w-4 text-center" style={{ color: "var(--color-text-secondary)" }}>{rows}</span>
          <button title="增加行数" onClick={() => rows < 5 && setRows(rows + 1)} className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}><Plus size={10} /></button>
          <span className="text-2xs mx-0.5" style={{ color: "var(--color-text-muted)" }}>×</span>
          <button title="减少列数" onClick={() => cols > 1 && setCols(cols - 1)} className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}><Minus size={10} /></button>
          <span className="text-2xs font-mono-num w-4 text-center" style={{ color: "var(--color-text-secondary)" }}>{cols}</span>
          <button title="增加列数" onClick={() => cols < 5 && setCols(cols + 1)} className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}><Plus size={10} /></button>
        </div>
        <div className="w-px h-3.5" style={{ background: "var(--color-border-secondary)" }} />
        {/* 比例 */}
        {ASPECT_RATIOS.map(r => (
          <button key={r} title={`画幅比例 ${r}`} onClick={() => setAspectRatio(r)} className="px-1.5 py-0.5 rounded-md text-2xs font-mono-num" style={{ background: aspectRatio === r ? accentBg() : "transparent", color: aspectRatio === r ? ACCENT : "var(--color-text-muted)" }}>
            {r}
          </button>
        ))}
      </div>

      {/* 参考图 */}
      {connectedImages.length > 0 && viewMode === "edit" && (
        <div className="flex items-center gap-1.5 px-3 py-1.5" style={{ borderBottom: "1px solid var(--color-border-secondary)" }}>
          <span className="text-label">参考</span>
          {connectedImages.map((img, i) => (
            <button key={i} onMouseDown={(e) => { e.preventDefault(); insertRefName(img.name || `参考图${i + 1}`); }}
              className="w-7 h-7 rounded-md overflow-hidden" style={{ border: "1px solid var(--color-border-primary)", opacity: focusedIndex !== null ? 1 : 0.5, cursor: focusedIndex !== null ? "pointer" : "default" }}
              title={focusedIndex !== null ? `插入 "${img.name}"` : "先点击输入框"}
            >
              <img src={img.url} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* 内容区 */}
      <div className="px-3 py-2 max-h-[320px] overflow-y-auto custom-scrollbar">
        {viewMode === "edit" && (
          <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
            {Array(totalCells).fill(null).map((_, i) => (
              <textarea
                key={i}
                ref={el => textareaRefs.current[i] = el}
                value={prompts[i] || ""}
                onChange={(e) => { const p = [...prompts]; p[i] = e.target.value; setPrompts(p); }}
                onFocus={() => setFocusedIndex(i)}
                onBlur={() => { setFocusedIndex(null); syncToData({ prompts }); }}
                placeholder={`格${i + 1}`}
                className="nodrag text-2xs rounded-md px-2 py-1.5 resize-none outline-none"
                style={{
                  background: "var(--color-bg-tertiary)",
                  border: focusedIndex === i ? `1px solid ${ACCENT}` : "1px solid var(--color-border-secondary)",
                  color: "var(--color-text-primary)",
                  minHeight: 48,
                }}
                rows={2}
              />
            ))}
          </div>
        )}

        {viewMode === "preview" && hasGridImage && (
          <div className="rounded-md overflow-hidden">
            <img src={gridImageUrl} alt="" className="w-full block" />
          </div>
        )}

        {viewMode === "generated" && hasSplitImages && (
          <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
            {splitCellImages.map((cell) => (
              <div key={cell.index} className="relative rounded-md overflow-hidden group" style={{ border: "1px solid var(--color-border-secondary)" }}>
                <img src={cell.url} alt="" className="w-full block" />
                <div className="absolute inset-0 bg-[var(--overlay-medium)] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                  <button onClick={() => handleRegenerateCell(cell.index)} className="p-1 rounded-md bg-[var(--overlay-light)]" title="重新生成"><RefreshCw size={10} style={{ color: "white" }} /></button>
                  <button onClick={() => handleExportCell(cell.index)} className="p-1 rounded-md bg-[var(--overlay-light)]" title="导出为节点"><ExternalLink size={10} style={{ color: "white" }} /></button>
                  <button onClick={() => handleUpscaleCell(cell.index)} className="p-1 rounded-md bg-[var(--overlay-light)]" title="高清放大"><ZoomIn size={10} style={{ color: "white" }} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 底部操作栏 */}
      <div className="flex items-center justify-between px-3 py-2" style={{ borderTop: "1px solid var(--color-border-secondary)" }}>
        <button title="收起面板" onClick={onClose} className="text-2xs px-2 h-6 rounded-md" style={{ color: "var(--color-text-muted)", background: "var(--color-bg-tertiary)" }}>
          收起
        </button>
        <div className="flex items-center gap-1.5">
          {hasGridImage && (
            <button
              title="按宫格拆分为多张图片"
              onClick={handleSplitGrid}
              disabled={isSplitting}
              className="flex items-center gap-1 px-2.5 h-8 rounded-md text-2xs font-medium"
              style={{ background: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}
            >
              {isSplitting ? <Loader2 size={11} className="animate-spin" /> : <Scissors size={11} />}
              拆分
            </button>
          )}
          <button
            title={filledCount === 0 ? "请先填写至少一格分镜描述" : "生成宫格分镜图"}
            onClick={handleGenerate}
            disabled={isGenerating || filledCount === 0}
            className="flex items-center gap-1 px-3 h-8 rounded-md text-2xs font-medium"
            style={{
              background: isGenerating ? "var(--color-bg-tertiary)" : ACCENT,
              color: isGenerating ? "var(--color-text-muted)" : "var(--color-accent-contrast)",
              cursor: isGenerating || filledCount === 0 ? "not-allowed" : "pointer",
            }}
          >
            {isGenerating ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
            生成
          </button>
        </div>
      </div>
    </div>
  );
}
